import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import DataSource from 'devextreme/data/data_source'
import TextBox from 'devextreme-react/text-box'
import notify from 'devextreme/ui/notify'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import Div from '@jumbo/shared/Div'
import LoadingButton from '@mui/lab/LoadingButton'
import { SelectBox } from 'devextreme-react/select-box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { duplicateClass } from 'app/redux/store/features/classes'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import CustomStore from 'devextreme/data/custom_store'
import utilServices from 'app/services/util-services'
import classesService from 'app/services/pages/courses/setup/classesServices/classesService'

const ClassDuplicateForm = ({ classId, termId, onRefresh, handleError }) => {
    const { hideDialog } = useJumboDialog()
    const dispatch = useDispatch()
    // Store the selected term id (SelectBox uses valueExpr='id').
    // Start empty so user must explicitly pick a term when duplicating.
    const [termSelection, setTermSelection] = useState('')
    const termLookupById = useCallback(async (key) => {
        try {
            return await termServices.getTermLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        // When opened without typing, DevExtreme may pass no searchValue.
                        // We still want current+future terms to show.
                        let searchTerm = loadOptions.searchValue ?? ''
                        if (typeof searchTerm === 'string') searchTerm = searchTerm.trim()
                        // If the API doesn't return anything without a search term, default to current year.
                        if (!searchTerm) searchTerm = String(new Date().getFullYear())
                        return await termLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()
                        return termLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )
    const [classSelection, setClassSelection] = useState('')
    const [termErrorState, setTermErrorState] = useState(false)

    const termLookupList = useCallback(async (searchTerm) => {
        try {
            return await termServices.getFutureTermLookupList({ termYear: searchTerm, past: false, future: true })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupList = useCallback(async (searchTerm) => {
        try {
            return await classesService.getClassLookupList({
                termId: termSelection,
                className: searchTerm,
            })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupById = useCallback(async (key) => {
        try {
            return await classesService.getClassLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await classLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return classLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const hideDialogAndRefreshList = () => {
        hideDialog()
        notify({ message: 'Class duplicated successfully', width: 300, position: 'top right' }, 'success', 1500)
        onRefresh()
    }

    const duplicateClassMutation = useMutation((data) => {
        dispatch(duplicateClass(data))
        hideDialogAndRefreshList()
    })

    useEffect(() => {
        // Preload so the dropdown has items on first open.
        termLookupsStore?.load?.()
    }, [termLookupsStore])

    const onFormSubmit = () => {
        if (!termSelection) {
            setTermErrorState(true)
            return
        }

        setTermErrorState(false)

        duplicateClassMutation.mutate({
            classId: classId,
            termId: termSelection,
            className: classSelection,
        })
    }

    return (
        <Div>
            <Div sx={{ borderBottom: 1, borderColor: 'success.main' }}>
                <Div sx={{ m: 3 }}>
                    <Typography>Source Class</Typography>
                    <SelectBox
                        height={40}
                        dataSource={classLookupsStore}
                        displayExpr={'className'}
                        value={classId}
                        readOnly={true}
                    />
                </Div>
            </Div>
            <Div sx={{ m: 3 }}>
                <Typography>Term Year / Number</Typography>
                <SelectBox
                    height={40}
                    dataSource={termLookupsStore}
                    valueExpr={'id'}
                    itemTemplate={(data) => data.termYear + ' / ' + data.termNumber}
                    displayExpr={(data) => (data ? data.termYear + ' / ' + data.termNumber : '')}
                    value={termSelection}
                    searchEnabled={true}
                    showDataBeforeSearch={true}
                    minSearchLength={0}
                    onOpened={(e) => {
                        // Ensure we have data even when user hasn't typed a search term.
                        try {
                            e?.component?.option?.('searchValue', '')
                        } catch (err) {
                            // ignore
                        }
                        e?.component?.getDataSource?.()?.load?.()
                    }}
                    onValueChange={(value) => {
                        setTermSelection(value)
                        setTermErrorState(false)
                    }}
                    placeholder='Select Term'
                    showClearButton={true}
                    isValid={!termErrorState}
                />
                {termErrorState && (
                    <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                        Please select a term year.
                    </Typography>
                )}
                <Div sx={{ mt: 1.5, mb: 5 }}>
                    <Typography>New Class Name</Typography>
                    <TextBox
                        value={classSelection}
                        height={40}
                        showClearButton={true}
                        placeholder='Enter class name'
                        onValueChange={setClassSelection}
                    />
                </Div>
            </Div>
            <Stack direction={'row'} spacing={2} sx={{ m: 'auto', ml: 10.5, mb: 2 }}>
                <LoadingButton
                    variant='contained'
                    color='success'
                    onClick={onFormSubmit}
                    size='large'
                    sx={{ width: 100 }}>
                    Save
                </LoadingButton>
                <LoadingButton variant='outlined' color='success' onClick={hideDialog} size='large'>
                    Cancel
                </LoadingButton>
            </Stack>
        </Div>
    )
}

export default ClassDuplicateForm
